<app-modal [isOpen]="isOpen" (close)="onClose()" className="max-w-3xl mx-4 sm:mx-6">
  @if (vm; as w) {
    <!--
      INC_CRM_020 (7) inc21 — `overflow-hidden` + `shrink-0` en cabecera/pestañas/pie y
      `min-h-0` en el cuerpo. Sin esto, el cuerpo (que en la pestaña «Línea de tiempo» crece con
      cada actualización) no podía encogerse por debajo de su contenido —`min-height:auto` es el
      valor por defecto de un ítem flex— y APLASTABA la fila de pestañas hasta dejarla en blanco,
      además de desbordar hacia el scroll del propio modal.
    -->
    <div class="flex flex-col max-h-[85vh] overflow-hidden">
      <!-- Header -->
      <div class="shrink-0 flex items-start justify-between gap-3 px-6 pt-5 pb-3 border-b border-gray-200 dark:border-gray-700">
        <div class="flex items-start gap-3 min-w-0">
          <span class="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg text-lg font-semibold text-white bg-brand-500">
            {{ initial }}
          </span>
          <div class="min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white break-words min-w-0">{{ w.name }}</h3>
              <app-badge [color]="statusColor" size="sm">{{ statusLabel }}</app-badge>
              @if (w.isTemplate) {
                <app-badge color="warning" size="sm">Plantilla</app-badge>
              }
            </div>
            <!--
              INC_CRM_020 (2) inc6 — descripción recortada a 3 líneas con "Ver más": el tope
              es de 5000 caracteres y sin esto el encabezado crecería sin control.
            -->
            @if (w.description) {
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 break-words whitespace-pre-wrap"
                 [class.line-clamp-3]="!descriptionExpanded">{{ w.description }}</p>
              @if (isDescriptionLong) {
                <button type="button" (click)="descriptionExpanded = !descriptionExpanded"
                        class="mt-1 text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400">
                  {{ descriptionExpanded ? 'Ver menos' : 'Ver más' }}
                </button>
              }
            }
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="shrink-0 flex border-b border-gray-200 dark:border-gray-700 px-6 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        @for (tab of tabs; track tab.id) {
          <button type="button" (click)="setTab(tab.id)"
            [class.border-brand-500]="activeTab === tab.id"
            [class.text-brand-600]="activeTab === tab.id"
            [class.border-transparent]="activeTab !== tab.id"
            [class.text-gray-500]="activeTab !== tab.id"
            class="px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap">
            {{ tab.label }}
          </button>
        }
      </div>

      <!-- Content -->
      <div class="flex-1 min-h-0 overflow-y-auto px-6 py-5">

        <!-- ===== INFORMACIÓN ===== -->
        @if (activeTab === 'info') {
          <div class="space-y-5">
            <section>
              <h4 class="text-xs font-semibold text-gray-500 uppercase mb-2">Información general</h4>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <p class="text-xs text-gray-500">Tipo de objeto base</p>
                  <p class="text-gray-800 dark:text-gray-200">{{ objectTypeLabel }}</p>
                </div>
                <div>
                  <p class="text-xs text-gray-500">Carpeta</p>
                  <p class="text-gray-800 dark:text-gray-200">{{ folderName }}</p>
                </div>
                <div>
                  <p class="text-xs text-gray-500">Responsable</p>
                  <p class="text-gray-800 dark:text-gray-200">{{ ownerName || '—' }}</p>
                </div>
                <div>
                  <p class="text-xs text-gray-500">Prioridad</p>
                  <p class="text-gray-800 dark:text-gray-200">{{ priorityOrder }}</p>
                </div>
              </div>
            </section>

            @if (tags.length) {
              <section>
                <h4 class="text-xs font-semibold text-gray-500 uppercase mb-2">Etiquetas</h4>
                <div class="flex flex-wrap gap-1.5">
                  @for (tag of tags; track tag) {
                    <span class="px-2 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                      {{ tag }}
                    </span>
                  }
                </div>
              </section>
            }

            <section>
              <h4 class="text-xs font-semibold text-gray-500 uppercase mb-2">Inscripción</h4>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <p class="text-xs text-gray-500">Re-inscripción</p>
                  <p class="text-gray-800 dark:text-gray-200">
                    {{ allowsReEnrollment ? 'Permitida' : 'Inscripción única' }}
                  </p>
                </div>
                <div>
                  <p class="text-xs text-gray-500">Máx. inscripciones activas</p>
                  <p class="text-gray-800 dark:text-gray-200">
                    {{ w.maxActiveEnrollments ?? 'Sin límite' }}
                  </p>
                </div>
                <div>
                  <p class="text-xs text-gray-500">Criterios de salida</p>
                  <p class="text-gray-800 dark:text-gray-200">
                    {{ exitCriteriaCount === 0 ? 'Sin criterios' : exitCriteriaCount + (exitCriteriaCount === 1 ? ' criterio' : ' criterios') }}
                  </p>
                </div>
                <div>
                  <p class="text-xs text-gray-500">Reglas de supresión</p>
                  <p class="text-gray-800 dark:text-gray-200">
                    {{ suppressionRulesCount === 0 ? 'Sin reglas' : suppressionRulesCount + (suppressionRulesCount === 1 ? ' regla' : ' reglas') }}
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h4 class="text-xs font-semibold text-gray-500 uppercase mb-2">Metadata</h4>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <p class="text-xs text-gray-500">Creado</p>
                  <p class="text-gray-800 dark:text-gray-200">{{ formatDate(w.datetimeCreated) }}</p>
                </div>
                <div>
                  <p class="text-xs text-gray-500">Última actualización</p>
                  <p class="text-gray-800 dark:text-gray-200">{{ formatDate(w.datetimeUpdated) }}</p>
                </div>
                @if (w.activatedAt) {
                  <div>
                    <p class="text-xs text-gray-500">Activado</p>
                    <p class="text-gray-800 dark:text-gray-200">{{ formatDate(w.activatedAt) }}</p>
                  </div>
                }
                @if (w.pausedAt) {
                  <div>
                    <p class="text-xs text-gray-500">Pausado</p>
                    <p class="text-gray-800 dark:text-gray-200">{{ formatDate(w.pausedAt) }}</p>
                  </div>
                }
                @if (w.archivedAt) {
                  <div>
                    <p class="text-xs text-gray-500">Archivado</p>
                    <p class="text-gray-800 dark:text-gray-200">{{ formatDate(w.archivedAt) }}</p>
                  </div>
                }
              </div>
            </section>
          </div>
        }

        <!-- ===== MONITOREO ===== -->
        @if (activeTab === 'monitoring') {
          <div class="space-y-4">
            <!-- Controles: rango, pruebas y recarga -->
            <div class="flex flex-wrap items-center gap-3">
              <div class="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                @for (d of monitoringRanges; track d) {
                  <button type="button" (click)="cambiarRangoMonitoreo(d)"
                          [ngClass]="monitoringRangeDays === d
                            ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'"
                          class="px-3 py-1 text-xs rounded-md transition-all font-medium">
                    {{ d }} días
                  </button>
                }
              </div>
              <label class="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" [(ngModel)]="monitoringIncludeTestRuns"
                       (ngModelChange)="cargarMonitoreo()"
                       class="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"/>
                <span class="text-xs text-gray-600 dark:text-gray-400">Incluir pruebas</span>
              </label>
              <button type="button" (click)="cargarMonitoreo()" [disabled]="monitoringLoading"
                      class="ml-auto px-3 py-1 text-xs rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors">
                {{ monitoringLoading ? 'Actualizando…' : 'Actualizar' }}
              </button>
            </div>

            @if (monitoringError) {
              <div class="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400">
                {{ monitoringError }}
              </div>
            } @else if (monitoringLoading && !monitoring) {
              <p class="text-sm text-gray-400 dark:text-gray-500 text-center py-8">Cargando monitoreo…</p>
            } @else if (monitoring) {
              <app-workflow-monitoring-summary
                [dashboard]="monitoring"
                [compact]="true"
                [showDetails]="true"
                [maxErrors]="3" />

              <p class="text-xs text-gray-400 dark:text-gray-500">
                Inscripciones de los últimos {{ monitoring.rangeDays }} días.
              </p>
            }

            <!-- La serie temporal y los registros inscritos ahora mismo viven en la página. -->
            <a [routerLink]="['/automation/workflows', w.id, 'monitoring']" (click)="onClose()"
               class="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400">
              Ver monitoreo completo
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </a>
          </div>
        }

        <!-- ===== LÍNEA DE TIEMPO ===== -->
        @if (activeTab === 'timeline') {
          <app-audit-timeline
            [logs]="timelineLogs"
            [loading]="timelineLoading"
            [error]="timelineError"
            [currentPage]="timelinePage"
            [totalPages]="timelineTotalPages"
            [totalElements]="timelineTotalElements"
            emptyMessage="No hay eventos de auditoría para este workflow."
            (pageChange)="cargarTimeline($event)" />
        }
      </div>

      <!--
        Pie. INC_CRM_020 (2) inc1 — solo lo contextual: las acciones de estado (activar,
        pausar, duplicar, guardar como plantilla, archivar) ya están en la fila de la lista
        y repetirlas aquí era justo lo que reportó QA.
      -->
      <div class="shrink-0 flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
        <button type="button" (click)="onEdit()"
          class="px-3 py-1.5 text-sm rounded-lg font-medium transition-colors bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700">
          Editar
        </button>
        <button type="button" (click)="onClose()"
          class="px-3 py-1.5 text-sm rounded-lg font-medium transition-colors bg-brand-500 text-white hover:bg-brand-600">
          Cerrar
        </button>
      </div>
    </div>
  }
</app-modal>
